import React, { useState, useCallback, useEffect } from 'react';
import { Store } from '../../services/database/vision';
import { FileText, ChevronLeft, ChevronRight, CheckCircle, X, Plus } from 'lucide-react';
import { PlanBadge } from '../ui/plan-badge';
import { Modal } from '../ui/modal';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

interface SettlementViewProps {
  stores: Store[];
  selectedDate: { month: number; year: number };
  onNavigateMonth: (direction: 'prev' | 'next') => void;
  formatMonthYear: (month: number, year: number) => string;
}

export function SettlementView({ 
  stores, 
  selectedDate, 
  onNavigateMonth,
  formatMonthYear 
}: SettlementViewProps) {
  const [showExportModal, setShowExportModal] = useState(false);
  const [showStoreSelector, setShowStoreSelector] = useState(false);
  const [completedStores, setCompletedStores] = useState<Store[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [totalValue, setTotalValue] = useState(0);
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Função para buscar lojas concluídas no período
  const loadCompletedStores = useCallback(() => {
    const completedInPeriod = stores.filter(store => {
      if (!store.completed_at) return false;
      const completedDate = new Date(store.completed_at);
      return completedDate.getMonth() + 1 === selectedDate.month && 
             completedDate.getFullYear() === selectedDate.year;
    });

    setCompletedStores(completedInPeriod);
    setSelectedStores(completedInPeriod); // Inicialmente, todas as lojas estão selecionadas
    
    // Calcular valor total
    const total = completedInPeriod.reduce((sum, store) => sum + (store.price || 0), 0);
    setTotalValue(total);
  }, [stores, selectedDate]);

  useEffect(() => {
    loadCompletedStores();
  }, [loadCompletedStores]);

  // Função para alternar seleção de loja
  const toggleStoreSelection = (store: Store) => {
    setSelectedStores(prev => {
      const isSelected = prev.some(s => s.id === store.id);
      const newSelection = isSelected
        ? prev.filter(s => s.id !== store.id)
        : [...prev, store];
      
      // Atualizar valor total
      const newTotal = newSelection.reduce((sum, s) => sum + (s.price || 0), 0);
      setTotalValue(newTotal);
      
      return newSelection;
    });
  };

  // Agrupar lojas por plano
  const groupStoresByPlan = (stores: Store[]) => {
    return stores.reduce((groups, store) => {
      const plan = store.plan.charAt(0).toUpperCase() + store.plan.slice(1);
      if (!groups[plan]) groups[plan] = [];
      groups[plan].push(store);
      return groups;
    }, {} as Record<string, Store[]>);
  };

  // Função para exportar dados como PDF
  const handleExportPDF = () => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    const groupedStores = groupStoresByPlan(selectedStores);
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 12;
    
    // Função para adicionar página
    const addPage = () => {
      doc.addPage();
      // Fundo escuro
      doc.setFillColor(17, 17, 17);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      return margin;
    };
    
    // Fundo escuro para a primeira página
    doc.setFillColor(17, 17, 17);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // Gradiente sutil no topo
    for (let i = 0; i < 40; i++) {
      const alpha = (40 - i) / 1000;
      doc.setFillColor(255, 255, 255);
      doc.setGState(new doc.GState({ opacity: alpha }));
      doc.rect(0, i, pageWidth, 1, 'F');
    }
    doc.setGState(new doc.GState({ opacity: 1 }));
    
    // Logo Jarvis
    const logoSize = 10;
    doc.setFillColor(34, 197, 94);
    doc.circle(margin + logoSize/2, margin + logoSize/2, logoSize/2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.text('J', margin + logoSize/2 - 3, margin + logoSize/2 + 4);
    
    // Título e período
    doc.setFontSize(24);
    doc.text('Relatório de Lojas', margin + logoSize + 8, margin + logoSize/2 + 3);
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(formatMonthYear(selectedDate.month, selectedDate.year).toLowerCase(), margin + logoSize + 8, margin + logoSize + 6);
    
    let yPos = margin + logoSize + 20;
    
    // Cards de métricas
    const cardWidth = (pageWidth - 2 * margin - 8) / 2;
    const cardHeight = 35;
    
    // Card 1 - Total de Lojas
    doc.setFillColor(24, 24, 24);
    doc.roundedRect(margin, yPos, cardWidth, cardHeight, 3, 3, 'F');
    
    // Círculo escuro para ícone
    doc.setFillColor(34, 34, 34);
    doc.circle(margin + cardWidth/4, yPos + cardHeight/2, cardHeight/4, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.text(selectedStores.length.toString(), margin + cardWidth/2, yPos + 18);
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text('Lojas Selecionadas', margin + cardWidth/2, yPos + 28);
    
    // Card 2 - Valor Total
    doc.setFillColor(24, 24, 24);
    doc.roundedRect(margin + cardWidth + 8, yPos, cardWidth, cardHeight, 3, 3, 'F');
    
    // Círculo escuro para ícone
    doc.setFillColor(34, 34, 34);
    doc.circle(margin + cardWidth + 8 + cardWidth/4, yPos + cardHeight/2, cardHeight/4, 'F');
    
    doc.setTextColor(34, 197, 94);
    doc.setFontSize(24);
    doc.text(`R$ ${totalValue.toLocaleString('pt-BR')}`, margin + cardWidth + 8 + cardWidth/2, yPos + 18);
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text('Valor Total', margin + cardWidth + 8 + cardWidth/2, yPos + 28);
    
    yPos += cardHeight + 20;

    // Iterar sobre os grupos
    Object.entries(groupedStores).forEach(([plan, stores], index) => {
      // Verificar se precisa de nova página
      if (yPos + 25 + stores.length * 12 > pageHeight - margin) {
        yPos = addPage();
      }
      
      // Card do plano
      doc.setFillColor(24, 24, 24);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 25 + stores.length * 12, 3, 3, 'F');
      
      // Badge do plano
      const planColor = plan === 'Branding' ? '#f59e0b' : plan === 'Premium' ? '#3b82f6' : '#22c55e';
      const [r, g, b] = hexToRgb(planColor);
      
      // Badge com fundo escuro
      doc.setFillColor(r * 0.2, g * 0.2, b * 0.2);
      doc.roundedRect(margin + 10, yPos + 8, 20, 12, 2, 2, 'F');
      
      // Texto do plano
      doc.setTextColor(r, g, b);
      doc.setFontSize(11);
      doc.text(plan.charAt(0).toUpperCase(), margin + 20, yPos + 16, { align: 'center' });
      
      // Nome do plano
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.text(plan, margin + 40, yPos + 16);
      
      // Quantidade e valor do plano
      const planTotal = stores.reduce((sum, store) => sum + store.price, 0);
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(11);
      doc.text(`${stores.length} lojas`, pageWidth - margin - 75, yPos + 16);
      doc.setTextColor(34, 197, 94);
      doc.text(`R$ ${planTotal.toLocaleString('pt-BR')}`, pageWidth - margin - 15, yPos + 16, { align: 'right' });
      
      yPos += 28;

      // Lista de lojas
      stores.forEach((store, idx) => {
        // Verificar se precisa de nova página
        if (yPos + 14 > pageHeight - margin) {
          yPos = addPage();
        }
        
        // Linha divisória ultra fina
        if (idx > 0) {
          doc.setDrawColor(40, 40, 40);
          doc.setLineWidth(0.1);
          doc.line(margin + 12, yPos - 2, pageWidth - margin - 12, yPos - 2);
        }

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.text(store.name, margin + 15, yPos + 5);
        
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(10);
        doc.text(new Date(store.completed_at!).toLocaleDateString('pt-BR'), pageWidth - margin - 75, yPos + 5);
        
        doc.setTextColor(34, 197, 94);
        const formattedValue = store.price === 0 ? '0' : `${store.price.toLocaleString('pt-BR')}`;
        doc.text(`R$ ${formattedValue}`, pageWidth - margin - 15, yPos + 5, { align: 'right' });
        
        yPos += 12;
      });
      
      yPos += 15;
    });
    
    doc.save(`lojas-concluidas-${selectedDate.month}-${selectedDate.year}.pdf`);
    setShowExportModal(false);
  };

  // Função auxiliar para converter hex para rgb
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Lojas Concluídas</h2>
          <p className="text-sm text-white/60">
            {selectedStores.length} de {completedStores.length} {completedStores.length === 1 ? 'loja selecionada' : 'lojas selecionadas'} em {formatMonthYear(selectedDate.month, selectedDate.year)}
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* Navegação de Meses */}
          <div className="flex items-center gap-2 bg-white/[0.03] rounded-xl p-1">
            <button
              onClick={() => onNavigateMonth('prev')}
              className="p-2 hover:bg-white/[0.08] rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <span className="text-lg font-medium capitalize px-3">
              {formatMonthYear(selectedDate.month, selectedDate.year)}
            </span>
            
            <button
              onClick={() => onNavigateMonth('next')}
              className="p-2 hover:bg-white/[0.08] rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-all"
            disabled={selectedStores.length === 0}
          >
            <FileText className="w-4 h-4" />
            Exportar Selecionadas
          </button>
        </div>
      </div>

      {/* Lista de Lojas Concluídas */}
      <div className="space-y-4">
        {completedStores.map(store => {
          const isSelected = selectedStores.some(s => s.id === store.id);
          
          return (
            <div
              key={store.id}
              className={`bg-white/[0.03] rounded-xl p-4 transition-all cursor-pointer ${
                isSelected 
                  ? 'bg-white/[0.06]' 
                  : 'hover:bg-white/[0.04]'
              }`}
              onClick={() => toggleStoreSelection(store)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${
                    isSelected ? 'bg-green-500/20' : 'bg-white/[0.08]'
                  }`}>
                    <CheckCircle className={`w-5 h-5 ${
                      isSelected ? 'text-green-400' : 'text-white/60'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-medium">{store.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <PlanBadge plan={store.plan} />
                      <span className="text-sm text-white/60">
                        Concluída em {new Date(store.completed_at!).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium">R$ {store.price.toLocaleString('pt-BR')}</div>
                  <div className="text-sm text-white/60">Valor do projeto</div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Botão para adicionar mais lojas */}
        <button
          onClick={() => setShowStoreSelector(true)}
          className="w-full py-4 bg-white/[0.03] rounded-xl border border-dashed border-white/[0.08] text-white/60 hover:text-white hover:bg-white/[0.06] transition-all flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Adicionar mais lojas
        </button>

        {completedStores.length === 0 && (
          <div className="text-center py-8 text-white/60">
            Nenhuma loja concluída neste período
          </div>
        )}
      </div>

      {/* Total */}
      {selectedStores.length > 0 && (
        <div className="mt-6 pt-6 border-t border-white/[0.08] flex justify-between items-center">
          <div className="text-lg">Total selecionado</div>
          <div className="text-2xl font-bold text-green-400">
            R$ {totalValue.toLocaleString('pt-BR')}
          </div>
        </div>
      )}

      {/* Modal de Exportação */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Exportar Lojas Concluídas"
      >
        <div className="space-y-6">
          <div>
            <h3 className="font-medium mb-2">Resumo da Exportação</h3>
            <p className="text-sm text-white/60">
              {selectedStores.length} {selectedStores.length === 1 ? 'loja selecionada' : 'lojas selecionadas'} em {formatMonthYear(selectedDate.month, selectedDate.year)}
            </p>
            <p className="text-sm text-white/60">
              Valor total: R$ {totalValue.toLocaleString('pt-BR')}
            </p>
          </div>

          {/* Lista de Lojas Selecionadas */}
          <div className="max-h-[200px] overflow-y-auto space-y-2">
            {selectedStores.map(store => (
              <div key={store.id} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>{store.name}</span>
                </div>
                <div className="text-sm text-white/60">
                  R$ {store.price.toLocaleString('pt-BR')}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowExportModal(false)}
              className="px-4 py-2 text-white/60 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all"
            >
              <FileText className="w-4 h-4" />
              Exportar PDF
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de Seleção de Lojas */}
      <Modal
        isOpen={showStoreSelector}
        onClose={() => setShowStoreSelector(false)}
        title="Adicionar Lojas"
      >
        <div className="space-y-6">
          {/* Busca */}
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar loja..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-white/[0.16]"
            />
          </div>

          {/* Lista de Lojas */}
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {stores
              .filter(store => 
                !selectedStores.some(s => s.id === store.id) &&
                store.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map(store => (
                <div
                  key={store.id}
                  className="flex items-center justify-between p-3 bg-white/[0.03] rounded-lg hover:bg-white/[0.06] cursor-pointer"
                  onClick={() => {
                    toggleStoreSelection(store);
                    if (!store.completed_at) {
                      // Se a loja não tiver data de conclusão, define para a data atual
                      store.completed_at = new Date().toISOString();
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    <Plus className="w-4 h-4 text-green-400" />
                    <div>
                      <div className="font-medium">{store.name}</div>
                      <div className="text-sm text-white/60">
                        <PlanBadge plan={store.plan} />
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-white/60">
                    R$ {store.price.toLocaleString('pt-BR')}
                  </div>
                </div>
              ))}
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setShowStoreSelector(false)}
              className="px-4 py-2 bg-white/[0.08] text-white rounded-xl hover:bg-white/[0.12] transition-all"
            >
              Concluir
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
